import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiChevronRight,
  FiHome,
  FiUsers,
  FiCheckCircle,
  FiClock,
  FiGift,
  FiLayers,
} from "react-icons/fi";
import toast from "react-hot-toast";

import {
  getAutoPoolStatus,
  distributePool,
} from "../../store/slices/pool";

const AutoPoolIncome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    autoPoolStatus,
    autoPoolStatusLoading,
    distributePoolLoading,
    error,
  } = useSelector((state) => state.pool);

  const [distributingLevel, setDistributingLevel] = useState(null);
  useEffect(() => {
    dispatch(getAutoPoolStatus());
  }, [dispatch]);

  const matrix = autoPoolStatus?.matrix || {};

  const pools = Array.isArray(autoPoolStatus?.pools)
    ? autoPoolStatus.pools
    : [];

  const formatNumber = (number) => {
    return Number(number || 0).toLocaleString("en-IN");
  };

  const handleDistribute = async (pool) => {
    try {
      setDistributingLevel(pool.level);
      const triggerPool = pool.level + 1;

      const response = await dispatch(
        distributePool({
          matrixCycleId: matrix?._id,
          poolLevel: triggerPool,
        })
      ).unwrap();

      toast.success(
        response?.message ||
        `Auto Pool ${pool.level} distributed successfully`
      );

      await dispatch(getAutoPoolStatus());
    } catch (error) {
      console.error("Pool distribution failed:", error);

      toast.error(
        typeof error === "string"
          ? error
          : error?.message || "Failed to distribute pool"
      );
    } finally {
      setDistributingLevel(null);
    }
  };

  const handleViewMembers = (pool) => {
    if (!pool?.level) {
      toast.error("Invalid pool level");
      return;
    }

    if (!pool?.activatedMembers || pool.activatedMembers <= 0) {
      toast.error("No members available in this pool");
      return;
    }

    navigate(`/dashboard/autopool-details/${pool.level}`);
  };

  /* =====================================================
     STATUS CLASS
  ===================================================== */

  const getStatusClass = (status) => {
    if (status?.toLowerCase() === "completed") {
      return "completed";
    }

    return "filling";
  };

  /* =====================================================
     DISTRIBUTION UI
  ===================================================== */

  const renderDistribution = (pool) => {
    const distribution =
      String(pool?.distribution || "").toLowerCase();

    const distributionStatus =
      String(pool?.distributionStatus || "").toLowerCase();

    /*
      ==========================================
      ALREADY DISTRIBUTED
      ==========================================
  
      Your API currently returns:
  
      distribution: "Distributed"
  
      So use this instead of prizeDistributed.
    */

    if (distribution === "distributed") {
      return (
        <button
          type="button"
          className="gi-pool-distribution-btn distributed"
          disabled
        >
          <FiCheckCircle />
          Distributed
        </button>
      );
    }

    /*
      ==========================================
      READY FOR DISTRIBUTION
      ==========================================
  
      API:
  
      completed: true
      distribution: "Ready for Distribution"
      distributionStatus: "WAITING"
    */

    const isReadyForDistribution =
      pool?.completed === true &&
      distribution === "ready for distribution" &&
      distributionStatus === "waiting";

    if (isReadyForDistribution) {
      const loading =
        distributePoolLoading &&
        distributingLevel === pool.level;

      return (
        <button
          type="button"
          className="gi-pool-distribution-btn distribute"
          disabled={loading}
          onClick={() => handleDistribute(pool)}
        >
          <FiGift />

          {loading
            ? "Distributing..."
            : "Distribute"}
        </button>
      );
    }

    /*
      ==========================================
      FALLBACK / STILL FILLING
      ==========================================
    */

    return (
      <button
        type="button"
        className="gi-pool-distribution-btn waiting"
        disabled
      >
        <FiClock />

        {pool?.distribution ||
          `Waiting Pool ${pool?.level}`}
      </button>
    );
  };



  return (
    <div className="gi-referral-page">

      {/* =====================================================
          BREADCRUMB
      ===================================================== */}

      <div className="gi-breadcrumb">

        <Link to="/dashboard">
          <FiHome />
          Dashboard
        </Link>

        <FiChevronRight className="gi-breadcrumb-arrow" />

        <span>
          Auto Pool
        </span>

      </div>


      <section className="gi-referral-table-card">



        <div className="gi-referral-table-wrapper">

          <table className="gi-referral-table">

            <thead>

              <tr>

                <th>
                  #
                </th>

                <th>
                  Auto Pool
                </th>

                <th>
                  Required Members
                </th>

                <th>
                  Activated Members
                </th>

                <th>
                  Remaining Members
                </th>

                <th>
                  Status
                </th>

                <th>
                  Members
                </th>

                <th>
                  Distribution
                </th>

              </tr>

            </thead>

            <tbody>

              {/* =================================================
                  LOADING
              ================================================= */}

              {autoPoolStatusLoading && (

                <tr>

                  <td
                    colSpan="8"
                    className="gi-table-message"
                  >
                    Loading auto pool status...
                  </td>

                </tr>

              )}

              {/* =================================================
                  ERROR
              ================================================= */}

              {!autoPoolStatusLoading && error && (

                <tr>

                  <td
                    colSpan="8"
                    className="gi-table-message"
                  >
                    {error}
                  </td>

                </tr>

              )}

              {/* =================================================
                  NO DATA
              ================================================= */}

              {!autoPoolStatusLoading &&
                !error &&
                pools.length === 0 && (

                  <tr>

                    <td
                      colSpan="8"
                      className="gi-table-message"
                    >
                      No auto pool data found
                    </td>

                  </tr>

                )}

              {/* =================================================
                  POOLS
              ================================================= */}

              {!autoPoolStatusLoading &&
                !error &&
                pools.map((pool, index) => (

                  <tr key={pool.level || index}>

                    {/* ================= SL NO ================= */}

                    <td>
                      {pool.slNo ||
                        String(index + 1).padStart(2, "0")}
                    </td>

                    {/* ================= AUTO POOL ================= */}

                    <td>

                      <strong className="gi-auto-pool-name">
                        {pool.autoPool}
                      </strong>

                    </td>

                    {/* ================= REQUIRED ================= */}

                    <td>

                      {formatNumber(
                        pool.requiredMembers
                      )}

                    </td>

                    {/* ================= ACTIVATED ================= */}

                    <td>

                      {formatNumber(
                        pool.activatedMembers
                      )}

                    </td>

                    {/* ================= REMAINING ================= */}

                    <td>

                      {formatNumber(
                        pool.remainingMembers
                      )}

                    </td>

                    {/* ================= STATUS ================= */}

                    <td>

                      <span
                        className={`gi-status-badge ${getStatusClass(
                          pool.status
                        )}`}
                      >

                        {pool.status === "Completed" ? (
                          <FiCheckCircle />
                        ) : (
                          <FiClock />
                        )}

                        {pool.status}

                      </span>

                    </td>

                    {/* ================= MEMBERS ================= */}

                    <td>

                      <button
                        type="button"
                        className={`gi-pool-members-btn ${pool.activatedMembers > 0
                          ? "active"
                          : "disabled"
                          }`}
                        disabled={
                          pool.activatedMembers <= 0
                        }
                        onClick={() =>
                          handleViewMembers(pool)
                        }
                      >


                        View Members

                      </button>

                    </td>

                    {/* ================= DISTRIBUTION ================= */}

                    <td>

                      {renderDistribution(pool)}

                    </td>

                  </tr>

                ))}

            </tbody>

          </table>

        </div>

      </section>

    </div>
  );
};

export default AutoPoolIncome;